import i18nFactory from '@yandex-int/i18n';
import { RadioButton } from 'lego-on-react';
import * as React from 'react';
import { ChangeEvent } from 'react';
import { Crop, PixelCrop } from 'react-image-crop';

import { BannerTextImageError, validateBannerTextImage } from '../../service/BannerHelpers/BannerHelpers';
import { Bem } from '../../service/Bem/Bem';
import { calcCropSelection, convertCropToPercentCrop } from '../../service/ImageHelpers/ImageHelpers';
import { Alert, AlertType } from '../Alert/Alert';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ImageCrop } from '../ImageCrop/ImageCrop';
import { ModalDecorator } from '../ModalDecorator/ModalDecorator';
import { Shape2Tone } from '../Shape2/Shape2';

import './BannerImageCrop.css';
import * as keyset from './BannerImageCrop.i18n';

const i18n = i18nFactory(keyset);

type BannerImageCropProps = {
    src: string;
    isOpen: boolean;
    onCrop: (blob: Blob, isNotCropped: boolean) => void;
    onClose: () => void;
    cropFormat: CropFormats;
    onCropFormatChange: (newCropFormat: CropFormats) => void;
};

type BannerImageCropState = {
    pixelCrop: PixelCrop;
    crop: Crop;
    imageSize?: {
        height: number;
        width: number;
    };
};

export enum CropFormats {
    ALL = 'all',
    WIDE = 'wide'
}

export class BannerImageCrop extends React.Component<BannerImageCropProps, BannerImageCropState> {
    block = 'banner-image-crop';

    CROP_DEFAULT_SETTINGS = {
        x: 0,
        y: 0,
        height: 100,
        width: 100
    };

    constructor(props: BannerImageCropProps) {
        super(props);

        this.state = {
            crop: this.CROP_DEFAULT_SETTINGS,
            pixelCrop: this.CROP_DEFAULT_SETTINGS
        };
    }

    render() {
        const { isOpen = false, src, cropFormat } = this.props;
        const { crop, pixelCrop } = this.state;
        const error = validateBannerTextImage(pixelCrop, cropFormat);

        return (
            <div className={Bem.cls(this.block)}>
                <ModalDecorator
                    cls={Bem.cls(this.block, 'popup')}
                    theme={'full-width-body-with-borders'}
                    visible={isOpen}
                    hasClose={true}
                    onClose={this.onPopupClose}
                    header={this.getPopupHeader()}
                    footer={this.getPopupFooter(error)}
                >
                    <div className={Bem.cls(this.block, 'popup-body')}>
                        <div className={Bem.cls(this.block, 'format-selector')}>
                            <RadioButton
                                size={'n'}
                                theme={'normal'}
                                view={'default'}
                                name={'format'}
                                value={cropFormat}
                                onChange={this.onFormatChange}
                            >
                                <RadioButton.Radio value={CropFormats.ALL}>{i18n('standart-format')}</RadioButton.Radio>
                                <RadioButton.Radio value={CropFormats.WIDE}>{i18n('wide-format')}</RadioButton.Radio>
                            </RadioButton>
                        </div>
                        <div className={Bem.cls(this.block, 'crop-wrapper-container')}>
                            <div className={Bem.cls(this.block, 'crop-wrapper')}>
                                <ImageCrop
                                    crop={crop}
                                    src={src}
                                    onComplete={this.onCropComplete}
                                    onImageLoaded={this.onImageLoaded}
                                />
                            </div>
                        </div>
                        {error ? (
                            <Alert type={AlertType.warning} iconTone={Shape2Tone.yellow}>
                                {this.getErrorText(error)}
                            </Alert>
                        ) : (
                            <div className={Bem.cls(this.block, 'crop-tip')}>{i18n('tip')}</div>
                        )}
                    </div>
                </ModalDecorator>
            </div>
        );
    }

    private getErrorText = (error: BannerTextImageError) => {
        switch (error) {
            case BannerTextImageError.MIN_SIZE_16_9:
                return i18n('min-size-16-9');
            case BannerTextImageError.MIN_SIZE:
                return i18n('min-size');
            case BannerTextImageError.WIDE_SCALE:
                return i18n('wide-scale');
            case BannerTextImageError.NARROW_SCALE:
                return i18n('narrow-scale');
            default:
                throw new Error('Unexpected bannerTextImage error!');
        }
    };

    private getPopupHeader = () => {
        return i18n('crop-title');
    };

    private getPopupFooter = (error?: BannerTextImageError) => {
        return (
            <div className={Bem.cls(this.block, 'footer')}>
                <ButtonDecorator
                    cls={Bem.cls(this.block, 'button')}
                    theme={'action'}
                    size={'n'}
                    text={i18n('save')}
                    disabled={Boolean(error)}
                    onClick={this.onSaveClick}
                />
                <ButtonDecorator
                    cls={Bem.cls(this.block, 'button')}
                    theme={'normal'}
                    size={'n'}
                    text={i18n('cancel')}
                    onClick={this.onPopupClose}
                />
            </div>
        );
    };

    private onFormatChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { imageSize = { height: 0, width: 0 } } = this.state;
        const value: CropFormats = e.target.value as CropFormats;

        this.setState(
            {
                crop: value === CropFormats.WIDE ? this.calcWideCrop(imageSize) : this.calcStandartCrop(imageSize)
            },
            () => {
                this.props.onCropFormatChange(value);
            }
        );
    };

    private onCropComplete = (crop: Crop, pixelCrop: PixelCrop) => {
        this.setState({
            crop,
            pixelCrop
        });
    };

    private calcStandartCrop = (size: { height: number; width: number }): Crop => {
        const imageRatio = size.width / size.height;

        if (imageRatio > 3 / 4 && imageRatio < 4 / 3) {
            return { x: 0, y: 0, height: 100, width: 100 };
        } else {
            return convertCropToPercentCrop(size, calcCropSelection(size, [3 / 4, 4 / 3]));
        }
    };

    private calcWideCrop = (size: { height: number; width: number }): Crop => {
        return {
            aspect: 16 / 9,
            ...convertCropToPercentCrop(size, calcCropSelection(size, [16 / 9]))
        };
    };

    private onImageLoaded = (image: HTMLImageElement, pixelCrop: PixelCrop) => {
        const imageRatio = image.naturalWidth / image.naturalHeight;
        let crop;

        if (imageRatio > 3 / 4 && imageRatio < 4 / 3) {
            crop = { x: 0, y: 0, height: 100, width: 100 };
        } else {
            crop = convertCropToPercentCrop(image, calcCropSelection(image, [3 / 4, 4 / 3]));
        }

        this.setState({
            crop,
            imageSize: {
                height: image.naturalHeight,
                width: image.naturalWidth
            }
        });
    };

    private onSaveClick = () => {
        const { crop, pixelCrop } = this.state;
        const { src, onCrop } = this.props;
        const image = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const isNotCropped = crop.x === 0 && crop.y === 0 && crop.height === 100 && crop.width === 100;

        image.src = src;

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        if (!ctx) {
            return;
        }

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        canvas.toBlob(blob => {
            if (blob) {
                onCrop(blob, isNotCropped);
            }
        }, 'image/jpeg');
    };

    private onPopupClose = () => {
        this.props.onClose();
    };
}
